import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { useRouter } from 'next/router';
import { isEmpty } from 'lodash';
import * as Styled from './Card.styled';
import { getCtaLayout } from './Card.utils';
import Content from './Content/Content';
import { handleNavigate } from '../../../../util/util';
import { ContainerQueryContext } from '../../../../util/ContainerQuery.context';
var DEFAULT_IMAGE_RESOLUTIONS = {
    small: 432,
    medium: 448,
    large: 516,
    xlarge: 670,
    largest: 1520
};
var Card = function (_a) {
    var _b, _c, _d, _e;
    var _f = _a.backgroundColor, backgroundColor = _f === void 0 ? undefined : _f, _g = _a.className, className = _g === void 0 ? '' : _g, _h = _a.ctas, ctas = _h === void 0 ? [] : _h, _j = _a.customBackgroundColor, customBackgroundColor = _j === void 0 ? '' : _j, secondaryHeader = _a.secondaryHeader, header = _a.header, headerMaxLineLength = _a.headerMaxLineLength, _k = _a.isTransparent, isTransparent = _k === void 0 ? false : _k, horizontalTextAlignment = _a.horizontalTextAlignment, icon = _a.icon, _l = _a.isMaxHeight, isMaxHeight = _l === void 0 ? false : _l, _m = _a.imageResolutions, imageResolutions = _m === void 0 ? DEFAULT_IMAGE_RESOLUTIONS : _m, responsiveTextSizes = _a.responsiveTextSizes, subheader = _a.subheader, _o = _a.cardTextColor, textColor = _o === void 0 ? 'AUTO' : _o, _p = _a.invertImageTextColor, invertImageTextColor = _p === void 0 ? false : _p, _q = _a.primaryHeaderPosition, textPosition = _q === void 0 ? 'TOP' : _q, _r = _a.secondaryHeaderPosition, secondaryHeaderPosition = _r === void 0 ? 'ABOVE_PRIMARY_HEADER' : _r, _s = _a.subheaderPosition, subheaderPosition = _s === void 0 ? 'BELOW_HEADERS' : _s, _t = _a.ctaPosition, ctaPosition = _t === void 0 ? 'BELOW_SUBHEADER' : _t, _u = _a.iconPosition, iconPosition = _u === void 0 ? 'ABOVE_HEADERS' : _u, _v = _a.ctaLayout, ctaLayout = _v === void 0 ? 'ONE_ON_CARD' : _v, settings = _a.settings, mobileImage = _a.mobileImage, tabletImage = _a.tabletImage, desktopImage = _a.desktopImage, internalSpace = _a.internalSpace, columnSpan = _a.columnSpan, hoverEffect = _a.hoverEffect, dataLabel = _a.dataLabel;
    var router = useRouter();
    var _w = useState(false), isRevealed = _w[0], setIsRevealed = _w[1];
    var styleQueryMode = useContext(ContainerQueryContext).styleQueryMode;
    var ref = useRef(null);
    var largeScreenImage = desktopImage || tabletImage || mobileImage;
    var mediumScreenImage = tabletImage || mobileImage || desktopImage;
    var smallScreenImage = mobileImage || tabletImage || desktopImage;
    var _x = getCtaLayout(ctaLayout, ctas), cardCtas = _x.cardCtas, overlayCtas = _x.overlayCtas;
    var shouldRenderAnchor = (cardCtas === null || cardCtas === void 0 ? void 0 : cardCtas.length) === 1 && !!cardCtas[0].url && !cardCtas[0].text;
    var isInteractable = (overlayCtas === null || overlayCtas === void 0 ? void 0 : overlayCtas.length) > 0 || (cardCtas === null || cardCtas === void 0 ? void 0 : cardCtas.length) > 0;
    useEffect(function () {
        var handleGlobalClick = function (e) {
            if (ref.current && !ref.current.contains(e.target))
                setIsRevealed(false);
        };
        var handleEscKeyPress = function (e) {
            if (e.key === 'Escape')
                setIsRevealed(false);
        };
        // close overlay if focus is moving outside the entire card
        var handleFocusOut = function (e) {
            if (ref.current &&
                !ref.current.contains(e.relatedTarget)) {
                setIsRevealed(false);
            }
        };
        if (isRevealed) {
            document.addEventListener('click', handleGlobalClick, {
                capture: true
            });
            document.addEventListener('keydown', handleEscKeyPress, {
                capture: true
            });
            // add focusout listener to the card element
            if (ref.current) {
                ref.current.addEventListener('focusout', handleFocusOut);
            }
        }
        return function () {
            document.removeEventListener('click', handleGlobalClick, {
                capture: true
            });
            document.removeEventListener('keydown', handleEscKeyPress, {
                capture: true
            });
            if (ref.current) {
                ref.current.removeEventListener('focusout', handleFocusOut);
            }
        };
    }, [isRevealed]);
    // if cards have overlay, render overlay, otherwise navigate to destination
    var onCardClick = function (e) {
        if ((overlayCtas === null || overlayCtas === void 0 ? void 0 : overlayCtas.length) > 0) {
            if (!isRevealed) {
                setIsRevealed(true);
            }
            if (shouldRenderAnchor && cardCtas[0].url) {
                window.location.href = cardCtas[0].url;
            }
        }
        else {
            e.preventDefault();
            var targetUrl = e.currentTarget.getAttribute('href') ||
                e.currentTarget.getAttribute('data-href');
            if (targetUrl)
                handleNavigate(router, targetUrl);
        }
    };
    var onCardKeyDown = function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
            onCardClick(e);
        }
    };
    var contentWrapper = useMemo(function () {
        return function (screenSize, columnSpanForScreenSize, image) {
            return (React.createElement(Content, { backgroundColor: backgroundColor, cardCtas: cardCtas, cardTextColor: textColor, invertImageTextColor: invertImageTextColor, columnSpan: columnSpanForScreenSize, ctaLayout: ctaLayout, customBackgroundColor: customBackgroundColor, secondaryHeader: secondaryHeader, header: header, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, icon: icon, image: image, imageResolutions: imageResolutions, internalSpace: internalSpace, isTransparent: isTransparent || backgroundColor === 'transparent', screenSize: screenSize, settings: settings, subheader: subheader, responsiveTextSizes: responsiveTextSizes, primaryHeaderPosition: textPosition, secondaryHeaderPosition: secondaryHeaderPosition, subheaderPosition: subheaderPosition, ctaPosition: ctaPosition, iconPosition: iconPosition }));
        };
    }, []);
    /**
     * Determines whether the provided position refers to an image overlay ("BELOW_IMAGE" or "ABOVE_IMAGE")
     * and that the associated content is present (non-empty).
     *
     * Content is considered present when:
     * - it is an array with at least one element, or
     * - for other types, it is truthy and not empty (as determined by a generic emptiness check).
     *
     * If the position is undefined or not one of the supported overlay positions, the result is false.
     *
     * @param position - The overlay position relative to the image; expected to be "BELOW_IMAGE" or "ABOVE_IMAGE".
     * @param content - The content to render in the overlay; may be any type, including arrays.
     * @returns True if the position is an image overlay position and the content is non-empty; otherwise, false.
     */
    var isImageOverlayPositionWithContent = function (position, content) {
        if (position === 'BELOW_IMAGE' || position === 'ABOVE_IMAGE') {
            if (!content || isEmpty(content))
                return false;
            if (Array.isArray(content))
                return content.length > 0;
            return true;
        }
        return false;
    };
    var cardBgColorByTextPosition = function (bgColor) {
        var elementsWithPosition = [
            { type: 'icon', position: iconPosition, content: icon },
            { type: 'primaryHeader', position: textPosition, content: header },
            { type: 'secondaryHeader', position: secondaryHeaderPosition, content: secondaryHeader },
            { type: 'subheader', position: subheaderPosition, content: subheader },
            { type: 'cta', position: ctaPosition, content: cardCtas }
        ];
        var hasOverlayPositioning = elementsWithPosition.some(function (_a) {
            var position = _a.position, content = _a.content;
            if (position === 'ABOVE_PRIMARY_HEADER' || position === 'BELOW_PRIMARY_HEADER') {
                var headerElement = elementsWithPosition.find(function (elm) { return elm.type === 'primaryHeader'; });
                return isImageOverlayPositionWithContent(headerElement === null || headerElement === void 0 ? void 0 : headerElement.position, content);
            }
            if (position === 'ABOVE_HEADERS' || position === 'BELOW_HEADERS') {
                var headerElement = elementsWithPosition.find(function (elm) { return elm.type === 'primaryHeader' || elm.type === 'secondaryHeader'; });
                return isImageOverlayPositionWithContent(headerElement === null || headerElement === void 0 ? void 0 : headerElement.position, content);
            }
            if (position === 'BELOW_SUBHEADER') {
                var subheaderElement = elementsWithPosition.find(function (elm) { return elm.type === 'subheader'; });
                if (subheaderElement && subheaderElement.position) {
                    if (subheaderElement.position === 'BELOW_HEADERS') {
                        var headerElement = elementsWithPosition.find(function (elm) { return elm.type === 'primaryHeader' || elm.type === 'secondaryHeader'; });
                        return isImageOverlayPositionWithContent(headerElement === null || headerElement === void 0 ? void 0 : headerElement.position, content);
                    }
                    return isImageOverlayPositionWithContent(subheaderElement === null || subheaderElement === void 0 ? void 0 : subheaderElement.position, content);
                }
                return false;
            }
            return isImageOverlayPositionWithContent(position, content);
        });
        return hasOverlayPositioning || !smallScreenImage ? bgColor : 'transparent';
    };
    return (React.createElement(Styled.Card, { "data-label": dataLabel || 'contentCard', backgroundColor: cardBgColorByTextPosition(backgroundColor), customBackgroundColor: cardBgColorByTextPosition(customBackgroundColor), verticalTextPosition: textPosition, textAlignment: horizontalTextAlignment, className: className, onClick: onCardClick, onKeyDown: onCardKeyDown, isInteractable: isInteractable, as: shouldRenderAnchor ? 'a' : 'div', "data-href": !shouldRenderAnchor && ((_b = cardCtas[0]) === null || _b === void 0 ? void 0 : _b.url)
            ? (_c = cardCtas[0].url) !== null && _c !== void 0 ? _c : ''
            : undefined, href: shouldRenderAnchor ? (_d = cardCtas[0].url) !== null && _d !== void 0 ? _d : '' : undefined, tabIndex: isInteractable ? 0 : undefined, ref: ref, isMaxHeight: isMaxHeight, isFullHeight: !((_e = smallScreenImage === null || smallScreenImage === void 0 ? void 0 : smallScreenImage.file) === null || _e === void 0 ? void 0 : _e.url) || textPosition === 'BELOW_IMAGE', hoverEffect: hoverEffect, "$styleQueryMode": styleQueryMode },
        React.createElement(Styled.CardContentLarge, { "$styleQueryMode": styleQueryMode }, contentWrapper('LARGE', columnSpan === null || columnSpan === void 0 ? void 0 : columnSpan.desktop, largeScreenImage)),
        React.createElement(Styled.CardContentMedium, { "$styleQueryMode": styleQueryMode }, contentWrapper('MEDIUM', columnSpan === null || columnSpan === void 0 ? void 0 : columnSpan.tablet, mediumScreenImage)),
        React.createElement(Styled.CardContentSmall, { "$styleQueryMode": styleQueryMode }, contentWrapper('SMALL', columnSpan === null || columnSpan === void 0 ? void 0 : columnSpan.mobile, smallScreenImage)),
        overlayCtas && isRevealed && (React.createElement(Styled.CardOverlay, { ctas: overlayCtas, onRequestClose: function () { return setIsRevealed(false); }, isTransparent: backgroundColor === 'transparent', responsiveTextSizes: responsiveTextSizes }))));
};
export default Card;
